'use client'

import { useEffect, useState, useMemo } from 'react'
import { useWalletClient, useChainId } from 'wagmi'
import { createPublicClient, http } from 'viem'
import { entryPoint07Address } from 'viem/account-abstraction'
import { createSmartAccountClient } from 'permissionless'
import { toSimpleSmartAccount } from 'permissionless/accounts'
import { createPimlicoClient } from 'permissionless/clients/pimlico'
import {
  isGasSponsorshipSupported,
  getApiKey,
  getChainGasSponsorshipConfig,
} from './config/gasSponsorship'
import { PIMLICO_API_KEY } from 'src/config/env'
import { chainByChainId, getCurrentRpcUrl } from 'src/helpers/constants'

/**
 * Hook for OP Sepolia gasless transactions using Pimlico
 * Provides gas sponsorship through Pimlico's infrastructure
 */
export const usePimlicoGaslessWallet = () => {
  const [smartAccountClient, setSmartAccountClient] = useState<any>(null)
  const [isInitializing, setIsInitializing] = useState(false)
  const [isReady, setIsReady] = useState(false)

  const { data: walletClient } = useWalletClient()
  const chainId = useChainId()

  const hasGasSponsorship = isGasSponsorshipSupported(chainId || 0)
  const chainConfig = getChainGasSponsorshipConfig(chainId || 0)
  const apiKey = getApiKey(chainId || 0) || PIMLICO_API_KEY

  // Pimlico bundler and paymaster URLs - now dynamic based on configuration
  const bundlerUrl =
    hasGasSponsorship && chainConfig && apiKey
      ? `${chainConfig.config.bundlerUrl}?apikey=${apiKey}`
      : null
  const paymasterUrl =
    hasGasSponsorship && chainConfig && apiKey
      ? `${chainConfig.config.paymasterUrl}?apikey=${apiKey}`
      : null

  const publicClient = useMemo(() => {
    if (!hasGasSponsorship || !chainConfig || !chainId) {
      return null
    }

    // Get the current chain object

    if (!chainConfig) {
      console.warn(`Chain ${chainId} not supported for Pimlico gas sponsorship`)
      return null
    }

    return createPublicClient({
      transport: http(getCurrentRpcUrl(chainId)),
      chain: chainByChainId[chainId],
    })
  }, [hasGasSponsorship, chainConfig, chainId])

  const initializeSmartAccount = async () => {
    if (
      !walletClient ||
      !publicClient ||
      !apiKey ||
      !hasGasSponsorship ||
      !bundlerUrl ||
      !paymasterUrl
    ) {
      return
    }

    setIsInitializing(true)

    try {
      // Create simple smart account from signer
      const simpleAccount = await toSimpleSmartAccount({
        client: publicClient,
        owner: walletClient,
        entryPoint: {
          address: entryPoint07Address,
          version: '0.7',
        },
      })

      // Create Pimlico paymaster client
      const paymasterClient = createPimlicoClient({
        transport: http(paymasterUrl),
        entryPoint: {
          address: entryPoint07Address,
          version: '0.7',
        },
      })

      // Create smart account client with bundler and paymaster
      const smartClient = createSmartAccountClient({
        account: simpleAccount,
        chain: chainByChainId[chainId || 0],
        bundlerTransport: http(bundlerUrl),
        paymaster: paymasterClient,
        userOperation: {
          estimateFeesPerGas: async () => {
            return (await paymasterClient.getUserOperationGasPrice()).fast
          },
        },
      })

      setSmartAccountClient(smartClient)
      setIsReady(true)
    } catch (error) {
      console.error('Failed to initialize smart account:', error)
      setIsReady(false)
    } finally {
      setIsInitializing(false)
    }
  }

  useEffect(() => {
    if (walletClient && hasGasSponsorship && apiKey) {
      initializeSmartAccount()
    } else {
      setSmartAccountClient(null)
      setIsReady(false)
    }
  }, [walletClient, hasGasSponsorship, apiKey])

  /**
   * Execute a gasless transaction using Pimlico
   */
  const executeGaslessTransaction = async (
    to: `0x${string}`,
    data: `0x${string}`,
    value: bigint = BigInt(0)
  ): Promise<`0x${string}`> => {
    if (!smartAccountClient || !isReady) {
      throw new Error('Smart account not ready')
    }

    try {
      const txHash = await smartAccountClient.sendTransaction({
        to,
        data,
        value,
      })

      return txHash
    } catch (error) {
      console.error('Gasless transaction failed:', error)
      throw error
    }
  }

  /**
   * Get the smart account address
   */
  const getSmartAccountAddress = (): `0x${string}` | null => {
    return smartAccountClient?.account?.address || null
  }

  return {
    isOPSepolia: hasGasSponsorship, // Now represents any chain with gas sponsorship
    isReady,
    isInitializing,
    smartAccountClient,
    smartAccountAddress: getSmartAccountAddress(),
    executeGaslessTransaction,
    hasGasSponsorship: isReady && !!apiKey,
  }
}
